<template>
  <form
    class="flex flex-col gap-sm pb-sm"
    id="arbetsgiare-form"
    @submit="attemptSubmit"
  >
    <FormGroup>
      <KontaktorsakInput
        v-model:kontaktorsakId="kontaktorsakId"
        v-model:fortydligadKontaktorsak="fortydligadKontaktorsak"
        :selectedSamtalsamneIds="values.samtalsamneIds || []"
        :disabled="props.arende && !!props.arende.kontaktorsakId"
        @push="pushSamtalsamne"
        @remove="removeSamtalsamne"
      />
    </FormGroup>
    <FormGroup>
      <InternaKommentarer
        v-model="internKommentar"
        :required="shouldRequireInternKommentar(values.lamnaOverTill)"
        :internaKommentarer="props.arende?.internaKommentarer ?? []"
        :show-verified="false"
        :can-edit="true"
      />
    </FormGroup>
    <FormGroup>
      <KanalInput
        v-model="kanal"
        required
        :disabled="!!props.arende"
        :options="availableKanalTypeOptions"
      />
      <KundgruppInput
        v-model="kundgrupp"
        :disabled="!!props.arende"
        :options="ArbetsgivareKundgruppOptions"
      />
      <OmbudInput v-model="ombud" :disabled="!!props.arende" />
    </FormGroup>
    <CollapsableFormGroup id="hantering" title="Hantering">
      <LamnaOverInput
        v-model="lamnaOverTill"
        :options="ArbetsgivareLamnaOverTillOptionGroups"
      />
      <NamnInput
        id="namn"
        v-model="namn"
        label="Namn - Kontaktuppgift i ärendet"
      />
      <EmailInput
        id="arendeemail"
        v-model="email"
        label="E-postadress - Kontaktuppgift i ärendet"
        :required="shouldRequireEmail(lamnaOverTill)"
      />
      <TelefonnummerInput
        id="arendetelefonnummer"
        v-model="telefonnummer"
        label="Telefonnummer - Kontaktuppgift i ärendet"
      />
      <div class="flex flex-col gap-none py-xs" v-if="props.arende?.id">
        <label for="copy-arende-url">Kopiera länk till ärende</label>
        <CopyToClipboard
          id="copy-arende-url"
          text="Klicka här för att kopiera ärendelänk"
          :data="composeArbetsgivareArendetUrl(props.arende.id)"
        />
      </div>
      <ArendetypInput v-model="arendetyp" />
    </CollapsableFormGroup>
    <avtalat-message v-if="arendeStore.arende.error" type="negative" small
      ><p>
        {{
          arendeStore.arende.error.errors ??
          "Något har gått fel, kontakta IT-drift."
        }}
      </p></avtalat-message
    >
    <div class="flex flex-row justify-end gap-xs">
      <avtalat-button
        id="save-button"
        v-if="saveButtonVisible"
        outlined
        :disabled="!meta.valid"
        :loading="isSubmitting"
        @click="setArendeCloseStatus(false)"
        >Spara</avtalat-button
      >
      <avtalat-button
        id="save-close-button"
        v-if="saveCloseButtonVisible"
        :disabled="!meta.valid"
        :loading="isSubmitting"
        @click="setArendeCloseStatus(true)"
        >Spara och stäng</avtalat-button
      >
    </div>
  </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/zod";
import { configure, useField, useFieldArray, useForm } from "vee-validate";
import { computed, inject, onBeforeUnmount, onMounted } from "vue";
import * as z from "zod";

import {
  AllKanalTypeOptions,
  InternalCommentErrorMessage,
} from "@/common/constants";
import {
  mapPuzzelQueryStringToKanalType,
  mapPuzzelQueryStringToKundgruppType,
} from "@/common/mappers";
import { composeArbetsgivareArendetUrl } from "@/common/utils/composeArbetsgivareArendetUrl";
import { isValidPhoneNumber } from "@/common/validators";
import {
  ArendetypInput,
  EmailInput,
  InternaKommentarer,
  KanalInput,
  KontaktorsakInput,
  KundgruppInput,
  LamnaOverInput,
  NamnInput,
  OmbudInput,
  TelefonnummerInput,
} from "@/components/form-components";
import CollapsableFormGroup from "@/components/ui/CollapsableFormGroup.vue";
import CopyToClipboard from "@/components/ui/CopyToClipboard.vue";
import FormGroup from "@/components/ui/FormGroup.vue";
import {
  Arbetsgivarearende,
  ArendeType,
  KanalType,
  KundgruppType,
  LamnaOverType,
} from "@/composables/KundserviceAppApiClient";
import { useApi } from "@/composables/useApi";
import { useNavigation } from "@/composables/useNavigation";
import { useConfigStore, useIndividStore } from "@/stores";

import { useArbetsgivareStore, useArbetsgivarearendenStore } from "../store";
import {
  ArbetsgivareKanalOptions,
  ArbetsgivareKundgruppOptions,
  ArbetsgivareLamnaOverTillOptionGroups,
} from "./arbetsgivareFormConstants";
import {
  shouldRequireEmail,
  shouldRequireInternKommentar,
  showSaveButton,
  showSaveCloseButton,
} from "./arbetsgivareFormRules";

configure({
  validateOnChange: true,
  validateOnInput: true,
  validateOnBlur: false,
});

type ArendeFormProps = {
  arende?: Arbetsgivarearende;
};

const props = defineProps<ArendeFormProps>();
const apiClient = useApi();
const navigation = useNavigation();
const configStore = useConfigStore();
const arendeStore = useArbetsgivarearendenStore();
const arbetsgivareStore = useArbetsgivareStore();
const individStore = useIndividStore();

const scrollToTop = inject("scrollToTop", () => {});
const openArendenAndReload = inject("openArendenAndReload", () => {});

onMounted(() => {
  if (scrollToTop) {
    scrollToTop();
  }
});

onBeforeUnmount(() => {
  arendeStore.arende.error = null;
});

const zKontaktorsak = z.string().min(1, "En kontaktorsak måste väljas");
const zFortydligadKontaktorsak = z
  .string()
  .max(1000, "Får inte överskrida 1000 tecken")
  .nullable()
  .optional()
  .or(z.literal(""))
  .superRefine((value, ctx) => {
    if (
      kontaktorsakId.value === configStore.getKontaktorsakOvrigt?.id &&
      !value
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Förtydliga kontaktorsak",
      });
    }
  });
const zSamtalsamne = z.string();
const zSamtalsamnen = z
  .array(zSamtalsamne)
  .max(6, "Får inte överskrida 6 övriga samtalsämnen")
  .optional();
const zInternKommentar = z
  .string()
  .max(1000, "Får inte överskrida 1000 tecken")
  .optional()
  .superRefine((value, ctx) => {
    if (shouldRequireInternKommentar(values.lamnaOverTill) && !value) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: InternalCommentErrorMessage(values.lamnaOverTill),
      });
    }
  });

const zNamn = z
  .string()
  .max(1000, "Får inte överskrida 1000 tecken")
  .optional()
  .or(z.literal(""));

const zEmail = z
  .string()
  .email({ message: "E-postadressen är inte giltig" })
  .optional()
  .or(z.literal(""));

const zTelefonnummer = z
  .string()
  .optional()
  .superRefine((value, ctx) => {
    if (value && !isValidPhoneNumber(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Telefonnumret har inte ett giltigt format",
      });
    }
  });

const formSchema = toTypedSchema(
  z.object({
    kontaktorsakId: zKontaktorsak,
    fortydligadKontaktorsak: zFortydligadKontaktorsak,
    samtalsamneIds: zSamtalsamnen,
    internKommentar: zInternKommentar,
    kanal: z.nativeEnum(KanalType),
    kundgrupp: z.nativeEnum(KundgruppType),
    ombud: z.boolean().optional(),
    arendetyp: z.nativeEnum(ArendeType),
    close: z.boolean().optional(),
    lamnaOverTill: z.nativeEnum(LamnaOverType),
    namn: zNamn,
    email: zEmail,
    telefonnummer: zTelefonnummer,
  }),
);

const { meta, values, handleSubmit, isSubmitting, validate, resetForm } =
  useForm({
    validationSchema: formSchema,
    initialValues: {
      kontaktorsakId: props.arende?.kontaktorsakId ?? "",
      fortydligadKontaktorsak: props.arende?.fortydligadKontaktorsak ?? "",
      samtalsamneIds: props.arende?.samtalsamneIds ?? [],
      internKommentar: "",
      kundgrupp:
        props.arende?.kundgrupp ??
        mapPuzzelQueryStringToKundgruppType(
          navigation.currentRoute.query.kundgrupp?.toString(),
        ) ??
        KundgruppType.Arbetsgivare,
      ombud: props.arende?.ombud ?? false,
      arendetyp: ArendeType.Kundservicearende,
      kanal:
        props.arende?.kanal ??
        mapPuzzelQueryStringToKanalType(
          navigation.currentRoute.query.mediakanal?.toString(),
        ),
      close: false,
      lamnaOverTill: LamnaOverType.Nej,
      namn: props.arende?.namn ?? "",
      email: props.arende?.email ?? "",
      telefonnummer: props.arende?.telefonnummer ?? "",
    },
  });

const { value: kontaktorsakId } = useField<string>("kontaktorsakId");
const { value: fortydligadKontaktorsak } = useField<string>(
  "fortydligadKontaktorsak",
);
const { remove: removeSamtalsamne, push: pushSamtalsamne } =
  useFieldArray<string>("samtalsamneIds");
const { value: internKommentar } = useField<string>("internKommentar");
const { value: kanal } = useField<KanalType>("kanal");
const { value: kundgrupp } = useField<KundgruppType>("kundgrupp");
const { value: arendetyp } = useField<ArendeType>("arendetyp");
const { setValue: setArendeCloseStatus } = useField<boolean>("close");
const { value: lamnaOverTill } = useField<LamnaOverType>("lamnaOverTill");
const { value: ombud } = useField<boolean>("ombud");
const { value: namn } = useField<string>("namn");
const { value: email } = useField<string>("email");
const { value: telefonnummer } = useField<string>("telefonnummer");

const saveButtonVisible = computed(() =>
  showSaveButton(values.lamnaOverTill, true),
);
const saveCloseButtonVisible = computed(() =>
  showSaveCloseButton(values.lamnaOverTill),
);

const availableKanalTypeOptions = computed(() => {
  return props.arende ? AllKanalTypeOptions : ArbetsgivareKanalOptions;
});

const attemptSubmit = handleSubmit(async () => {
  const { valid } = await validate();
  if (!valid) {
    return;
  }
  await onSubmit();
});

const onSubmit = handleSubmit(async () => {
  if (props.arende?.id) {
    await arendeStore.updateArende(
      {
        puzzelId:
          navigation.currentRoute.query.externalpuzzelid?.toString() ??
          undefined,
        arendeNumber: props.arende?.id,
        arendetyp: values.arendetyp,
        internKommentar: values.internKommentar,
        samtalsamneIds: values.samtalsamneIds,
        isClosed: values.close,
        lamnaOverTill: values.lamnaOverTill,
        namn: values.namn,
        email: values.email,
        telefonnummer: values.telefonnummer,
      },
      apiClient,
    );

    if (!arendeStore.arende.error) {
      openArendenAndReload();
      resetForm();
      navigation.closeArendeView();
    }
  } else {
    const arende = await arendeStore.createArende(
      {
        oragnisationsnummer: arbetsgivareStore.arbetsgivare.foretag
          ?.organisationsnummer as string,
        puzzelId:
          navigation.currentRoute.query.externalpuzzelid?.toString() ??
          undefined,
        kanal: values.kanal!,
        kundgrupp: values.kundgrupp,
        arendetyp: values.arendetyp,
        internalNote: values.internKommentar,
        kontaktorsakId: values.kontaktorsakId,
        kontaktorsakNamn:
          configStore.getKontaktorsakById(values.kontaktorsakId)?.namn ?? "",
        fortydligadKontaktorsak: values.fortydligadKontaktorsak,
        samtalsamneIds: values.samtalsamneIds,
        isClosed: values.close,
        isVerified: individStore.verification.isVerified,
        lamnaOverTill: values.lamnaOverTill,
        ombud: values.ombud,
        namn: values.namn,
        email: values.email,
        telefonnummer: values.telefonnummer,
      },
      apiClient,
    );

    if (arende) {
      resetForm();
      openArendenAndReload();
    }
  }
});
</script>
